import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { AnchoredPortal, useAnchoredStyle } from './AnchoredPortal';
import { useMenuKeyboard } from './useMenuKeyboard';
import { gateControlProps } from '@/utils/gateControl';
import type { WorkAreaGateType } from '@/utils/workAreaPermissionGate';
import { RefusalDescription, useRefusalNote } from './RefusalNote';

export interface ISidebarMenuEntry {
  label: string;
  title: string;
  onSelect: () => void;
  /** A permission answer (SB-3): a refused entry is disabled with its reason, never hidden. */
  gate?: WorkAreaGateType;
  /** A state of the list, not a permission: an arrow at an edge, an order still unread. */
  disabled?: boolean;
}

/**
 * U-0024 (IP-27, `docs/concept-mobile-app.md`): a sidebar line's actions on the iPad, behind one
 * 44 px button. With a finger each action is a 44 px target, 8 px from the next, and the sidebar is
 * 256 px: a namespace heading's three create and settings icons, hidden until a tap and still laid
 * out, left its name about 50 px and put its move arrows on a line of their own; the Work areas
 * heading's three icons left its words 64 of the 80 px they take. The menu holds the same actions;
 * a pointer keeps its icons in the row.
 */
export function SidebarActionsMenu({
  label,
  menuLabel,
  icon,
  entries,
}: {
  /** The trigger's accessible name ("More for acme", "New"). */
  label: string;
  /** The menu's own name. */
  menuLabel: string;
  icon: ReactNode;
  entries: ISidebarMenuEntry[];
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelStyle = useAnchoredStyle(triggerRef, panelRef, open, {
    placement: 'bottom',
    align: 'right',
  });

  useEffect(() => {
    if (!open) return undefined;
    const closeOnPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', closeOnPointerDown);
    return () => document.removeEventListener('pointerdown', closeOnPointerDown);
  }, [open]);
  useMenuKeyboard({ open, close: () => setOpen(false), menuRef: panelRef, triggerRef });

  if (entries.length === 0) return null;
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-sidebar-actions-menu=""
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={(event) => {
          // Inside the heading's own row, whose tap reveals a work area's handle: this is not it.
          event.stopPropagation();
          setOpen(!open);
        }}
        className="grid h-touch w-touch shrink-0 place-items-center rounded-md text-gray-500 outline-none focus-visible:ring-2 focus-visible:ring-focus dark:text-gray-400"
      >
        {icon}
      </button>
      {open ? (
        <AnchoredPortal>
          <div
            ref={panelRef}
            role="menu"
            aria-label={menuLabel}
            style={panelStyle}
            onKeyDown={(event) => event.stopPropagation()}
            className="z-[60] w-52 rounded-md bg-[#F0F1F2] p-1 shadow-lg ring-1 ring-black/5 dark:bg-[#242424] dark:ring-white/10"
          >
            {entries.map((entry) => (
              <SidebarMenuEntry
                key={entry.label}
                entry={entry}
                onSelect={() => {
                  setOpen(false);
                  entry.onSelect();
                }}
              />
            ))}
          </div>
        </AnchoredPortal>
      ) : null}
    </>
  );
}

const PERMITTED: WorkAreaGateType = { state: 'permitted', basis: 'granted' };

/**
 * One entry of the menu. A refused one (SB-3) stays in the menu, disabled, its reason in its `title`
 * (a tap shows it, `RefusedTouchHint`) and by `aria-describedby` (I-1678, SB-9).
 */
function SidebarMenuEntry({ entry, onSelect }: { entry: ISidebarMenuEntry; onSelect: () => void }) {
  const gate = entry.gate ?? PERMITTED;
  const refusal = useRefusalNote(gate);
  return (
    <>
      <button
        type="button"
        role="menuitem"
        aria-label={entry.label}
        onClick={onSelect}
        {...gateControlProps(gate, { otherwiseDisabled: entry.disabled, title: entry.title })}
        {...refusal.controlProps}
        className="flex h-touch w-full cursor-pointer items-center rounded px-3 text-left text-[14px] text-gray-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-default disabled:opacity-40 aria-disabled:cursor-default aria-disabled:opacity-50 dark:text-gray-200"
      >
        {entry.label}
      </button>
      {/* Beside the entry, not in it, so the entry's text stays its label. */}
      <RefusalDescription id={refusal.id} reason={refusal.reason} />
    </>
  );
}
